// Векторна заглушка обкладинки (SVG): «книга» з корінцем і торцем сторінок,
// на обкладинці — велика ілюстрація типу видання, що виходить за край.
// Колір/ілюстрація — за типом видання (getCoverKind), кольори — у CoverArt.css
// (світла і темна тема). Домен каталогу — тільки на великій версії (large).
// Ілюстрації — статичні рядки без даних користувача, тому dangerouslySetInnerHTML безпечний.
import { useId } from 'react';
import './CoverArt.css';

// Тип видання → ключ заглушки (логіка перенесена з колишньої getCoverIcon() в App.js)
export function getCoverKind(type, db) {
  const t = (type || '').toLowerCase();
  const d = (db || '').toUpperCase();
  if (d === 'KST') return 'article';
  if (d === 'KAD') return 'diss';
  if (d === 'KWR') return 'electronic';
  if (t.includes('карт') || t === 'ze') return 'map';
  if (t.includes('нот') || t.includes('муз') || t === 'zc') return 'music';
  if (t.includes('відео') || t.includes('video')) return 'video';
  if (t.includes('звук') || t.includes('аудіо')) return 'audio';
  if (t.includes('електрон') || t === 'zl' || t === 'ext') return 'electronic';
  if (t.includes('зображ') || t.includes('графік') || t === 'zk') return 'image';
  if (t.includes('журнал') || t === '02') return 'journal';
  if (t.includes('газет') || t === '01') return 'newspaper';
  if (t.includes('стат') || t === 'as') return 'article';
  if (t.includes('дисерт') || t.includes('автореф')) return 'diss';
  if (t === '03') return 'multivolume';
  return 'book';
}

// Стопка томів (багатотомне видання): книги лежать корінцями до нас, з-під кожної звисає закладка
const MULTIVOLUME = [
  { x: 22, y: 94, w: 150, fill: 'var(--ca-deep)', band: 'var(--ca-soft)', rib: 64, rot: 0 },
  { x: 34, y: 68, w: 132, fill: 'var(--ca-soft)', band: 'var(--ca-deep)', rib: 30, rot: 0 },
  { x: 28, y: 42, w: 140, fill: 'var(--ca-deep)', band: 'var(--ca-soft)', rib: 52, rot: -4 },
].map(b => `
  <g transform="rotate(${b.rot} ${b.x + b.w / 2} ${b.y + 13})">
    <polygon points="${b.x + b.rib},${b.y + 20} ${b.x + b.rib + 9},${b.y + 20} ${b.x + b.rib + 9},${b.y + 44} ${b.x + b.rib + 4.5},${b.y + 39} ${b.x + b.rib},${b.y + 44}" fill="var(--ca-pages)" stroke="var(--ca-spine)" stroke-width="1"/>
    <rect x="${b.x}" y="${b.y}" width="${b.w}" height="26" rx="5" fill="${b.fill}" stroke="var(--ca-cover)" stroke-width="2"/>
    <rect x="${b.x + 8}" y="${b.y + 4}" width="4" height="18" rx="2" fill="${b.band}"/>
    <rect x="${b.x + 16}" y="${b.y + 4}" width="4" height="18" rx="2" fill="${b.band}"/>
    <rect x="${b.x + 30}" y="${b.y + 9}" width="40" height="8" rx="3" fill="var(--ca-pages)"/>
  </g>`).join('');

const filmHoles = y => [0, 18, 36, 54, 72, 90, 108, 126, 144, 162]
  .map(x => `<rect x="${x}" y="${y}" width="8" height="7" rx="1"/>`).join('');

// Ілюстрації (viewBox 0 0 120 170), обрізаються clipPath по обкладинці
const ILLU = {
  book: `
    <circle cx="100" cy="88" r="52" fill="var(--ca-soft)" opacity=".7"/>
    <circle cx="36" cy="40" r="14" fill="var(--ca-soft)" opacity=".5"/>
    <path d="M26 166 C 48 148 66 176 90 160 S 130 146 160 162" fill="none" stroke="var(--ca-deep)" stroke-width="3" stroke-linecap="round"/>
    <circle cx="118" cy="168" r="3" fill="var(--ca-deep)"/>
    <path d="M58 132 C 52 84 98 26 156 -8 C 150 48 112 104 68 140 Z" fill="var(--ca-deep)"/>
    <path d="M62 136 Q 104 72 154 -4" fill="none" stroke="var(--ca-cover)" stroke-width="2.2" stroke-linecap="round"/>
    <g stroke="var(--ca-cover)" stroke-width="3" stroke-linecap="round">
      <path d="M62 104 l -8 -2"/><path d="M76 74 l -8 -3"/><path d="M118 70 l 8 1"/>
    </g>
    <polygon points="55,134 68,143 40,170" fill="var(--ca-spine)"/>`,
  journal: `
    <g transform="rotate(-14 85 125)"><rect x="40" y="72" width="92" height="118" rx="5" fill="var(--ca-soft)"/></g>
    <g transform="rotate(-4 85 125)">
      <rect x="46" y="68" width="92" height="118" rx="5" fill="var(--ca-pages)"/>
      <rect x="58" y="82" width="58" height="9" rx="3" fill="var(--ca-deep)"/>
      <rect x="58" y="100" width="70" height="5" rx="2" fill="var(--ca-soft)"/>
      <rect x="58" y="112" width="64" height="5" rx="2" fill="var(--ca-soft)"/>
      <rect x="58" y="124" width="70" height="5" rx="2" fill="var(--ca-soft)"/>
      <rect x="58" y="136" width="50" height="5" rx="2" fill="var(--ca-soft)"/>
    </g>`,
  diss: `
    <path d="M38 104 L38 152 Q85 176 132 152 L132 104 L85 124 Z" fill="var(--ca-deep)" opacity=".7"/>
    <polygon points="8,92 85,56 162,92 85,128" fill="var(--ca-deep)"/>
    <path d="M85 92 L24 110 L24 148" fill="none" stroke="var(--ca-soft)" stroke-width="4" stroke-linecap="round"/>
    <rect x="17" y="146" width="14" height="22" rx="4" fill="var(--ca-soft)"/>`,
  electronic: `
    <circle cx="96" cy="118" r="64" fill="var(--ca-soft)"/>
    <g fill="none" stroke="var(--ca-deep)" stroke-width="4">
      <circle cx="96" cy="118" r="64"/>
      <ellipse cx="96" cy="118" rx="28" ry="64"/>
      <line x1="96" y1="54" x2="96" y2="182"/>
      <line x1="32" y1="118" x2="160" y2="118"/>
      <line x1="43" y1="86" x2="149" y2="86"/>
      <line x1="43" y1="150" x2="149" y2="150"/>
    </g>`,
  article: `
    <g transform="rotate(8 95 115)">
      <rect x="40" y="50" width="100" height="134" rx="4" fill="var(--ca-pages)"/>
      <rect x="52" y="64" width="60" height="9" rx="3" fill="var(--ca-deep)"/>
      <rect x="52" y="82" width="76" height="5" rx="2" fill="var(--ca-soft)"/>
      <rect x="52" y="94" width="70" height="5" rx="2" fill="var(--ca-soft)"/>
      <rect x="48" y="106" width="84" height="16" rx="3" fill="var(--ca-soft)"/>
      <rect x="52" y="110" width="74" height="6" rx="2" fill="var(--ca-deep)"/>
      <rect x="52" y="130" width="76" height="5" rx="2" fill="var(--ca-soft)"/>
      <rect x="52" y="142" width="60" height="5" rx="2" fill="var(--ca-soft)"/>
    </g>`,
  newspaper: `
    <g transform="rotate(-6 95 115)">
      <rect x="28" y="46" width="124" height="140" rx="3" fill="var(--ca-pages)"/>
      <rect x="38" y="56" width="104" height="10" rx="2" fill="var(--ca-deep)"/>
      <rect x="38" y="76" width="46" height="34" rx="2" fill="var(--ca-soft)"/>
      <g fill="var(--ca-soft)">
        <rect x="92" y="76" width="50" height="4" rx="2"/><rect x="92" y="86" width="50" height="4" rx="2"/>
        <rect x="92" y="96" width="50" height="4" rx="2"/><rect x="92" y="106" width="36" height="4" rx="2"/>
        <rect x="38" y="120" width="46" height="4" rx="2"/><rect x="38" y="130" width="46" height="4" rx="2"/>
        <rect x="38" y="140" width="40" height="4" rx="2"/>
        <rect x="92" y="120" width="50" height="4" rx="2"/><rect x="92" y="130" width="50" height="4" rx="2"/>
        <rect x="92" y="140" width="44" height="4" rx="2"/>
      </g>
    </g>`,
  map: `
    <g transform="rotate(-10 95 115)">
      <polygon points="30,60 62,70 62,180 30,170" fill="var(--ca-soft)"/>
      <polygon points="62,70 94,60 94,170 62,180" fill="var(--ca-deep)" opacity=".45"/>
      <polygon points="94,60 126,70 126,180 94,170" fill="var(--ca-soft)"/>
      <polygon points="126,70 158,60 158,170 126,180" fill="var(--ca-deep)" opacity=".45"/>
    </g>
    <path d="M36 158 C 62 128 86 150 104 112" fill="none" stroke="var(--ca-spine)" stroke-width="3" stroke-dasharray="6 5" stroke-linecap="round"/>
    <path d="M108 112 C 94 94 94 76 108 76 C 122 76 122 94 108 112 Z" fill="var(--ca-spine)"/>
    <circle cx="108" cy="88" r="5" fill="var(--ca-cover)"/>`,
  music: `
    <g transform="rotate(-15 90 110)" stroke="var(--ca-soft)" stroke-width="2.5">
      <line x1="-20" y1="88" x2="210" y2="88"/><line x1="-20" y1="98" x2="210" y2="98"/>
      <line x1="-20" y1="108" x2="210" y2="108"/><line x1="-20" y1="118" x2="210" y2="118"/>
      <line x1="-20" y1="128" x2="210" y2="128"/>
    </g>
    <g fill="var(--ca-deep)">
      <ellipse cx="56" cy="146" rx="15" ry="10" transform="rotate(-20 56 146)"/>
      <ellipse cx="118" cy="128" rx="15" ry="10" transform="rotate(-20 118 128)"/>
      <rect x="66" y="58" width="5" height="88"/>
      <rect x="128" y="40" width="5" height="88"/>
      <polygon points="66,58 133,40 133,56 66,74"/>
    </g>`,
  video: `
    <g transform="rotate(-18 95 110)">
      <rect x="-10" y="66" width="220" height="74" fill="var(--ca-deep)"/>
      <g fill="var(--ca-cover)">${filmHoles(71)}${filmHoles(128)}</g>
      <g fill="var(--ca-soft)">
        <rect x="6" y="86" width="40" height="34" rx="2"/><rect x="54" y="86" width="40" height="34" rx="2"/>
        <rect x="102" y="86" width="40" height="34" rx="2"/><rect x="150" y="86" width="40" height="34" rx="2"/>
      </g>
    </g>
    <circle cx="96" cy="146" r="24" fill="var(--ca-cover)"/>
    <polygon points="88,134 108,146 88,158" fill="var(--ca-deep)"/>`,
  audio: `
    <g fill="var(--ca-soft)" transform="translate(0 -22)">
      <rect x="26" y="104" width="12" height="36" rx="6"/>
      <rect x="44" y="80" width="12" height="84" rx="6"/>
      <rect x="62" y="56" width="12" height="132" rx="6"/>
      <rect x="80" y="88" width="12" height="68" rx="6"/>
      <rect x="98" y="66" width="12" height="112" rx="6"/>
      <rect x="116" y="96" width="12" height="52" rx="6"/>
      <rect x="134" y="76" width="12" height="92" rx="6"/>
    </g>
    <g fill="var(--ca-deep)" stroke="var(--ca-cover)" stroke-width="3" stroke-linejoin="round">
      <path d="M66 34 L74 34 C 78 54 100 60 96 90 C 90 76 82 70 74 68 L74 130 L66 130 Z"/>
      <ellipse cx="54" cy="132" rx="20" ry="14" transform="rotate(-20 54 132)"/>
    </g>`,
  image: `
    <g transform="rotate(-6 95 110)">
      <rect x="28" y="46" width="130" height="122" rx="4" fill="var(--ca-pages)"/>
      <rect x="38" y="56" width="110" height="102" rx="2" fill="var(--ca-soft)"/>
      <circle cx="122" cy="82" r="13" fill="var(--ca-pages)"/>
      <polygon points="38,158 78,104 104,136 124,114 148,140 148,158" fill="var(--ca-deep)"/>
    </g>`,
  multivolume: MULTIVOLUME,
};

const THIN = ['journal', 'article', 'newspaper'];
const MASTHEAD = ['journal', 'newspaper'];

function buildSvgBody(kind, large, clipId) {
  const thin = THIN.includes(kind);
  const spineW = thin ? 6 : 13;
  const pagesW = thin ? 4 : 9;
  const coverX = 4;
  const coverW = 110 - pagesW;
  const illuX = coverX + spineW;
  const innerW = coverW - spineW;
  const masthead = MASTHEAD.includes(kind) ? `
    <rect x="${illuX}" y="3" width="${innerW}" height="30" fill="var(--ca-deep)"/>
    <rect x="${illuX + 10}" y="12" width="46" height="6" rx="2" fill="var(--ca-cover)"/>
    <rect x="${illuX + 10}" y="23" width="28" height="3" rx="1.5" fill="var(--ca-cover)" opacity=".7"/>` : '';
  const domain = large ? `
    <rect x="${illuX}" y="150" width="${innerW}" height="17" fill="var(--ca-cover)" opacity=".88"/>
    <text x="${illuX + innerW / 2}" y="161.5" text-anchor="middle" font-size="7" font-weight="600"
          letter-spacing=".4" fill="var(--ca-deep)">ec.udu.edu.ua</text>` : '';
  const pageLines = thin ? '' :
    `<path d="M${110 - pagesW + 2} 20 V150 M${110 - pagesW + 5} 20 V150" stroke="var(--ca-soft)" stroke-width=".8"/>`;
  return `
    <defs><clipPath id="${clipId}"><rect x="${illuX}" y="3" width="${innerW}" height="164"/></clipPath></defs>
    <rect x="${110 - pagesW}" y="7" width="${pagesW + 4}" height="156" rx="2" fill="var(--ca-pages)"/>
    ${pageLines}
    <rect x="${coverX}" y="3" width="${coverW}" height="164" rx="4" fill="var(--ca-cover)"/>
    <g clip-path="url(#${clipId})">${masthead}${ILLU[kind] || ILLU.book}${domain}</g>
    <rect x="${coverX}" y="3" width="${spineW}" height="164" rx="3" fill="var(--ca-spine)"/>
    <line x1="${coverX + spineW}" y1="3" x2="${coverX + spineW}" y2="167" stroke="#000" stroke-opacity=".15"/>`;
}

// kind — готовий ключ; або type+db — визначимо самі
export default function CoverArt({ kind, type, db, large = false }) {
  const k = ILLU[kind] ? kind : getCoverKind(type, db);
  const clipId = 'ca' + useId().replace(/[^a-zA-Z0-9]/g, '');
  return (
    <svg className={`cover-art cover-art--${k}`} viewBox="0 0 120 170"
         aria-hidden="true" focusable="false"
         dangerouslySetInnerHTML={{ __html: buildSvgBody(k, large, clipId) }} />
  );
}
